import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, BellRing, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/components/admin/adminApi";

/**
 * VAPID public key is base64url-encoded — Web Push requires a raw Uint8Array.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Notification opt-in card / button.
 *
 * Handles:
 *  - Feature detection (serviceWorker + PushManager + Notification)
 *  - Permission request
 *  - Subscribe via ServiceWorkerRegistration.pushManager.subscribe with VAPID key
 *  - POST /api/push/subscribe
 *  - Unsubscribe / re-enable
 *
 * variant:
 *   - "card"   : big card for the mobile ProfileScreen / dashboard
 *   - "inline" : compact button (for e.g. header)
 */
export default function NotificationOptIn({ variant = "card", testId = "notif-opt-in" }) {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [publicKey, setPublicKey] = useState(null);

  // Feature-detection + current subscription check on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || typeof Notification === "undefined") {
        if (!cancelled) setSupported(false);
        return;
      }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch { /* ignore */ }
      // Fetch VAPID public key
      try {
        const r = await api.get("/push/public-key");
        if (!cancelled) setPublicKey(r.data.public_key);
      } catch { /* server not configured */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = useCallback(async () => {
    if (!publicKey) { toast.error("Notifications indisponibles pour le moment"); return; }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Vous devez autoriser les notifications dans votre navigateur");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // Reuse an existing sub if any, else create one
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      // Send to backend
      await api.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_"),
          auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_"),
        },
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notifications activées");
      // Fire a self-test push so the user immediately sees the notification
      try { await api.post("/push/test", {}); } catch { /* non-blocking */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Impossible d'activer les notifications");
    } finally { setLoading(false); }
  }, [publicKey]);

  const disable = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications désactivées");
    } catch (err) {
      toast.error(err.message || "Erreur");
    } finally { setLoading(false); }
  }, []);

  if (!supported) return null;

  const blocked = permission === "denied";

  if (variant === "inline") {
    return subscribed ? (
      <Button
        onClick={disable}
        variant="outline"
        size="sm"
        className="rounded-none h-9 text-xs border-green-300 text-green-700 hover:bg-green-50"
        disabled={loading}
        data-testid={`${testId}-off`}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <BellRing className="w-3.5 h-3.5 mr-1" />}
        Notifications actives
      </Button>
    ) : (
      <Button
        onClick={enable}
        variant="outline"
        size="sm"
        className="rounded-none h-9 text-xs border-[#662483] text-[#662483] hover:bg-[#F0E9F3]"
        disabled={loading || blocked}
        data-testid={`${testId}-on`}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Bell className="w-3.5 h-3.5 mr-1" />}
        {blocked ? "Bloqué (paramètres nav.)" : "Activer les notifications"}
      </Button>
    );
  }

  // "card" variant — full block for profile screens / dashboards
  return (
    <div className={`border ${subscribed ? "border-green-200 bg-green-50" : blocked ? "border-red-200 bg-red-50" : "border-gray-200 bg-white"} p-4`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${subscribed ? "bg-green-100" : blocked ? "bg-red-100" : "bg-[#F0E9F3]"}`}>
          {subscribed ? <BellRing className="w-5 h-5 text-green-700" /> : blocked ? <BellOff className="w-5 h-5 text-red-600" /> : <Bell className="w-5 h-5 text-[#662483]" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#3F3F3F] leading-tight">
            {subscribed ? "Rappels de mission activés" : blocked ? "Notifications bloquées" : "Rappels de mission J-1"}
          </p>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            {subscribed
              ? "Vous recevrez un rappel la veille de chaque mission (18:00) avec l'entreprise, le poste et le taux horaire."
              : blocked
              ? "Les notifications sont bloquées pour ce site. Rendez-vous dans les paramètres de votre navigateur pour les réactiver."
              : "Recevez un rappel la veille de chaque mission (avec entreprise + taux horaire). Fonctionne sur mobile installé et desktop."}
          </p>
          <div className="mt-3">
            {subscribed ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-xs text-green-700 font-semibold">
                  <Check className="w-3.5 h-3.5" /> Actives
                </span>
                <Button
                  onClick={disable}
                  size="sm"
                  variant="outline"
                  disabled={loading}
                  className="rounded-none h-8 text-xs"
                  data-testid={`${testId}-disable`}
                >
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Désactiver"}
                </Button>
              </div>
            ) : (
              <Button
                onClick={enable}
                disabled={loading || blocked}
                size="sm"
                className="rounded-none h-9 bg-gradient-to-r from-[#662483] to-[#0069B4] text-white text-xs font-semibold"
                data-testid={`${testId}-enable`}
              >
                {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Activation…</> : <><Bell className="w-3.5 h-3.5 mr-1" /> Activer les rappels</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
